import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Download,
  Calendar,
  Users,
  Building2,
  Radio,
  Globe,
  Clock,
  Target,
  Zap,
  SmilePlus,
  CheckCircle,
  DollarSign,
  Award,
  ExternalLink,
} from 'lucide-react';
import {
  performanceService,
  PerformanceScorecard as PerformanceScorecardType,
  TimeWindow,
  AggregationLevel,
  MetricValue,
} from '../../services/performance.service';
import { useAuthStore } from '../../stores/auth.store';
import MetricDrilldownModal from './MetricDrilldownModal';

export default function PerformanceScorecard() {
  const user = useAuthStore((state) => state.user);
  const [scorecard, setScorecard] = useState<PerformanceScorecardType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('this_week');
  const [aggregationLevel, setAggregationLevel] = useState<AggregationLevel>('overall');
  const [entityId] = useState<string | undefined>(undefined);
  const [customStart, setCustomStart] = useState<string>('');
  const [customEnd, setCustomEnd] = useState<string>('');

  // Drilldown modal
  const [drilldownMetric, setDrilldownMetric] = useState<string | null>(null);

  useEffect(() => {
    loadScorecard();
  }, [user?.organizationId, timeWindow, aggregationLevel, entityId, customStart, customEnd]);

  const loadScorecard = async () => {
    if (!user?.organizationId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await performanceService.getScorecard({
        organizationId: user.organizationId,
        aggregationLevel,
        entityId,
        timeWindow,
        customStart: customStart || undefined,
        customEnd: customEnd || undefined,
      });
      setScorecard(data);
    } catch (err) {
      console.error('Failed to load performance scorecard:', err);
      // Use demo data instead of showing error
      setScorecard(getDemoScorecard(timeWindow, aggregationLevel));
    } finally {
      setIsLoading(false);
    }
  };

  const getDemoScorecard = (window: TimeWindow, level: AggregationLevel): PerformanceScorecardType => {
    const timeLabels: Record<TimeWindow, string> = {
      today: 'Today',
      yesterday: 'Yesterday',
      this_week: 'This Week (Jan 8-14)',
      last_week: 'Last Week (Jan 1-7)',
      this_month: 'This Month (January)',
      last_month: 'Last Month (December)',
      custom: 'Custom Range',
    };

    return {
      aggregationLevel: level,
      entityId: level === 'overall' ? undefined : 'demo-entity-001',
      entityName: level === 'overall' ? undefined : 'Demo Entity',
      timeWindow: {
        start: '2026-01-08T00:00:00Z',
        end: '2026-01-14T23:59:59Z',
        label: timeLabels[window],
      },
      metrics: {
        wfmAdherence: {
          current: 87.5,
          previous: 89.8,
          delta: -2.56,
          trend: 'down',
          unit: '%',
          scheduledHours: 5600,
          actualHours: 4900,
          adherenceRate: 87.5,
        },
        occupancy: {
          current: 78.3,
          previous: 75.1,
          delta: 4.26,
          trend: 'up',
          unit: '%',
          totalHours: 5600,
          productiveHours: 4385,
          occupancyRate: 78.3,
        },
        productivity: {
          current: 12.8,
          previous: 11.4,
          delta: 12.28,
          trend: 'up',
          unit: '',
          totalSolves: 56140,
          totalHours: 4385,
          solvesPerHour: 12.8,
        },
        csat: {
          current: 4.3,
          previous: 4.1,
          delta: 4.88,
          trend: 'up',
          unit: '',
          totalResponses: 1842,
          satisfiedCount: 1623,
          averageScore: 4.3,
        },
        sla: {
          current: 92.4,
          previous: 88.7,
          delta: 4.17,
          trend: 'up',
          unit: '%',
          totalContacts: 58240,
          metSLA: 53806,
          complianceRate: 92.4,
        },
        costPerContact: {
          current: 4.25,
          previous: 4.68,
          delta: -9.19,
          trend: 'down',
          unit: '$',
          totalCost: 247520,
          totalContacts: 58240,
          costPerContact: 4.25,
        },
        qaRating: {
          current: 88.5,
          previous: 86.2,
          delta: 2.67,
          trend: 'up',
          unit: '',
          totalReviews: 245,
          averageScore: 88.5,
          passRate: 91.8,
        },
      },
    };
  };

  const handleExportCSV = () => {
    if (scorecard) {
      performanceService.exportToCSV(scorecard);
    }
  };

  const handleMetricClick = (metricName: string) => {
    setDrilldownMetric(metricName);
  };

  const timeWindowOptions: Array<{ value: TimeWindow; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' },
  ];

  const aggregationOptions: Array<{ value: AggregationLevel; label: string; icon: any }> = [
    { value: 'overall', label: 'Overall', icon: Globe },
    { value: 'channel', label: 'By Channel', icon: Radio },
    { value: 'team', label: 'By Team', icon: Building2 },
    { value: 'agent', label: 'By Agent', icon: Users },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading performance data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-red-200 p-8">
        <div className="flex items-center justify-center text-red-600">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!scorecard) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Performance Scorecard</h2>
            <p className="text-gray-600 mt-1">
              Track key metrics across your organization
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export CSV</span>
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Time Window */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Time Window
            </label>
            <select
              value={timeWindow}
              onChange={(e) => setTimeWindow(e.target.value as TimeWindow)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {timeWindowOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Aggregation Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Aggregation Level
            </label>
            <select
              value={aggregationLevel}
              onChange={(e) => setAggregationLevel(e.target.value as AggregationLevel)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {aggregationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Date Range (only show when custom is selected) */}
          {timeWindow === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </>
          )}
        </div>

        {/* Time Window Display */}
        <div className="mt-4 text-sm text-gray-600">
          Showing data for: <span className="font-medium">{scorecard.timeWindow.label}</span>
          {scorecard.entityName && (
            <span className="ml-2">
              • Entity: <span className="font-medium">{scorecard.entityName}</span>
            </span>
          )}
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* WFM Adherence */}
        <MetricCard
          title="WFM Adherence"
          icon={Clock}
          metric={scorecard.metrics.wfmAdherence}
          color="purple"
          isPositiveGood={true}
          onClick={() => handleMetricClick('wfmAdherence')}
        />

        {/* Occupancy */}
        <MetricCard
          title="Occupancy"
          icon={Target}
          metric={scorecard.metrics.occupancy}
          color="blue"
          isPositiveGood={true}
          onClick={() => handleMetricClick('occupancy')}
        />

        {/* Productivity */}
        <MetricCard
          title="Productivity"
          subtitle="Solves per Hour"
          icon={Zap}
          metric={scorecard.metrics.productivity}
          color="green"
          isPositiveGood={true}
          onClick={() => handleMetricClick('productivity')}
        />

        {/* CSAT */}
        <MetricCard
          title="CSAT Score"
          icon={SmilePlus}
          metric={scorecard.metrics.csat}
          color="pink"
          isPositiveGood={true}
          onClick={() => handleMetricClick('csat')}
        />

        {/* SLA Compliance */}
        <MetricCard
          title="SLA Compliance"
          icon={CheckCircle}
          metric={scorecard.metrics.sla}
          color="indigo"
          isPositiveGood={true}
          onClick={() => handleMetricClick('sla')}
        />

        {/* Cost per Contact */}
        <MetricCard
          title="Cost per Contact"
          icon={DollarSign}
          metric={scorecard.metrics.costPerContact}
          color="orange"
          isPositiveGood={false}
          onClick={() => handleMetricClick('costPerContact')}
        />

        {/* QA Rating */}
        <MetricCard
          title="QA Rating"
          icon={Award}
          metric={scorecard.metrics.qaRating}
          color="teal"
          isPositiveGood={true}
          onClick={() => handleMetricClick('qaRating')}
        />
      </div>

      {/* Drilldown Modal */}
      {drilldownMetric && user?.organizationId && (
        <MetricDrilldownModal
          metricName={drilldownMetric}
          organizationId={user.organizationId}
          aggregationLevel={aggregationLevel}
          timeWindow={timeWindow}
          entityId={entityId}
          customStart={customStart}
          customEnd={customEnd}
          onClose={() => setDrilldownMetric(null)}
        />
      )}
    </div>
  );
}

interface MetricCardProps {
  title: string;
  subtitle?: string;
  icon: any;
  metric: MetricValue;
  color: string;
  isPositiveGood: boolean;
  onClick: () => void;
}

function MetricCard({ title, subtitle, icon: Icon, metric, color, isPositiveGood, onClick }: MetricCardProps) {
  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="h-4 w-4" />;
      case 'down':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <Minus className="h-4 w-4" />;
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'neutral') => {
    if (trend === 'neutral') return 'text-gray-500';
    const isGood = isPositiveGood ? trend === 'up' : trend === 'down';
    return isGood ? 'text-green-600' : 'text-red-600';
  };

  const colorClasses: Record<string, string> = {
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    pink: 'bg-pink-100 text-pink-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    orange: 'bg-orange-100 text-orange-600',
    teal: 'bg-teal-100 text-teal-600',
  };

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all text-left group"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
        <ExternalLink className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}

        <div className="flex items-baseline space-x-2">
          <span className="text-3xl font-bold text-gray-900">
            {metric.unit === '$' && metric.unit}
            {metric.current.toFixed(2)}
            {metric.unit !== '$' && metric.unit}
          </span>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className={`flex items-center space-x-1 ${getTrendColor(metric.trend)}`}>
            {getTrendIcon(metric.trend)}
            <span className="text-sm font-medium">
              {Math.abs(metric.delta).toFixed(1)}%
            </span>
          </div>
          <span className="text-xs text-gray-500">
            vs. previous: {metric.unit === '$' && metric.unit}{metric.previous.toFixed(2)}{metric.unit !== '$' && metric.unit}
          </span>
        </div>
      </div>
    </button>
  );
}
